/**
 * CensusSection — district gender census (2021), the Rails-era section
 * restored (estates-enrichment PR-3).
 *
 * Data: the generated district-census lookup (DATA.GOV.HK 2021 census
 * SSOT manifest), keyed by the wire's `district_name.en`. A district
 * outside the 18 official census districts renders nothing (Rails
 * `PopulationCensusService` semantics).
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import { CENSUS_YEAR, districtCensus } from '@/shared/lib/estates/district-census_gen';
import { projectLocalized, type LocalizedStringData } from '@letsgethome/lib/i18n';
import { plaintextString, type Locale } from '@letsgethome/types/branded';

type Props = {
  districtName: LocalizedStringData;
  locale: Locale;
};

// External attribution links (PR-B4, Rails census_section parity — the Rails
// footnote hyperlinked DATA.GOV.HK and its Terms and Conditions). There is no
// established SSOT lane for external third-party URLs (the constants_gen lane
// carries first-party/CDN origins only), so they live as named consts here,
// next to their single consumer.
const DATA_GOV_HK_URL = 'https://data.gov.hk';
const DATA_GOV_HK_TERMS_URL = 'https://data.gov.hk/en/terms-and-conditions';

/** One decimal place, matching the Rails service's `.round(1)`. */
function pct(part: number, total: number): number {
  if (total === 0) {
    return 0;
  }
  return Math.round((part / total) * 1000) / 10;
}

export function CensusSection({ districtName, locale }: Props) {
  const t = useTranslations('estates.detail.census');
  const census = districtCensus(districtName.en);
  if (!census) {
    return null;
  }
  const total = census.males + census.females;
  const malePct = pct(census.males, total);
  const femalePct = pct(census.females, total);
  // The BAR widths are complementary by construction (the two label
  // percentages are rounded independently and can sum to 99.9/100.1 —
  // fine for text, but the track must not over/underflow).
  const femaleBarPct = total === 0 ? 0 : 100 - malePct;
  const district = projectLocalized(districtName, locale);

  return (
    <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h2 className="text-2xl font-bold mb-2">{t('heading', { year: CENSUS_YEAR })}</h2>
      <p className="text-muted-foreground mb-6">
        {t('genderHeading', { district: plaintextString(district) })}
      </p>
      <div className="rounded-xl bg-muted p-6">
        <div className="flex h-4 w-full overflow-hidden rounded-full">
          <div className="bg-info-text h-full" style={{ width: `${malePct}%` }} />
          <div className="bg-success-text h-full" style={{ width: `${femaleBarPct}%` }} />
        </div>
        <div className="mt-4 flex justify-between text-sm">
          <span>
            <span className="bg-info-text mr-2 inline-block h-3 w-3 rounded-full align-middle" />
            {t('male')} {plaintextString(`${census.males.toLocaleString(locale)} (${malePct}%)`)}
          </span>
          <span>
            {t('female')}{' '}
            {plaintextString(`${census.females.toLocaleString(locale)} (${femalePct}%)`)}
            <span className="bg-success-text ml-2 inline-block h-3 w-3 rounded-full align-middle" />
          </span>
        </div>
        {/* Semantic census table (PR-B4 — Rails census_section/table_component
            parity): gender / population / percentage. The bar above stays the
            primary viz; the table is the accessible, exact-figures surface. */}
        <table className="mt-6 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('table.gender')}
              </th>
              <th scope="col" className="py-2 pr-4 text-right font-medium">
                {t('table.population')}
              </th>
              <th scope="col" className="py-2 text-right font-medium">
                {t('table.percentage')}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border/50">
              <th scope="row" className="py-2 pr-4 text-left font-normal">
                {t('male')}
              </th>
              <td className="py-2 pr-4 text-right">
                {plaintextString(census.males.toLocaleString(locale))}
              </td>
              <td className="py-2 text-right">{plaintextString(`${malePct}%`)}</td>
            </tr>
            <tr>
              <th scope="row" className="py-2 pr-4 text-left font-normal">
                {t('female')}
              </th>
              <td className="py-2 pr-4 text-right">
                {plaintextString(census.females.toLocaleString(locale))}
              </td>
              <td className="py-2 text-right">{plaintextString(`${femalePct}%`)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        {t('dataSource', { year: CENSUS_YEAR })}
        {' · '}
        <a
          href={DATA_GOV_HK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-foreground"
        >
          {t('attribution.dataGovHk')}
        </a>
        {' · '}
        <a
          href={DATA_GOV_HK_TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-foreground"
        >
          {t('attribution.terms')}
        </a>
      </p>
    </section>
  );
}
